import {
  type ChartReviewResponse,
  type FeedbackRequest,
  newBlankFeedbackRequest,
} from '@ctw/shared/api/ai/types';
import { useFeedback } from '@ctw/shared/api/ai/use-feedback';
import { useSummariesForPatient } from '@ctw/shared/api/ai/use-precompute-summary';

import useConfig from '@ctw/shared/api/ai/use-config';
import { Button } from '@ctw/shared/components/button';
import { Card } from '@ctw/shared/components/card';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { NoDataError } from '@ctw/shared/components/errors/no-data-error';
import { FeedbackThumbs } from '@ctw/shared/components/feedback-thumbs';
import { Heading } from '@ctw/shared/components/heading';
import SetTimeProgressBar from '@ctw/shared/components/indicators/set-time-progress-bar';
import { TwoColumnContent } from '@ctw/shared/components/layout/two-column-content';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { usePatientSummaryDrawer } from '@ctw/shared/content/chart-review/patient-summary-drawer';
import { PrecomputedSummary } from '@ctw/shared/content/chart-review/precomputed-summary';
import {
  type OverviewResources,
  PatientOverview,
} from '@ctw/shared/content/overview/patient-overview';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { formatDateToLocalString } from '@ctw/shared/utils/dates';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faSidebar, faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
export interface PatientSummaryProps {
  className?: ClassName;
}

export const PatientSummary = withErrorBoundary({
  boundaryName: 'PatientSummary',
  Component: ({ className }: PatientSummaryProps) => {
    const { patient } = usePatientContext();
    const telemetry = useTelemetry();

    const bryansFinalGiftToOurBlessedUi = useFeatureFlag('ctw-focused-summary');
    const useRegenerateSummary = useFeatureFlag('ctw-regenerate-summary');

    const { requestContext } = useCTW();

    const configs = useConfig(requestContext.builderId || '');

    const [displaySummaryType, setDisplaySummaryType] = useState<string | null>(null);

    // Effect to set the initial/default summary type once configs are loaded
    useEffect(() => {
      // If the new AI API is enabled, set the default summary type from the configs
      if (configs.configs && displaySummaryType === null) {
        if (bryansFinalGiftToOurBlessedUi) {
          setDisplaySummaryType(configs.configs?.DefaultSummarySlug ?? 'general');
        } else {
          setDisplaySummaryType('new_patient');
        }
      }
    }, [configs.configs, bryansFinalGiftToOurBlessedUi, displaySummaryType]);

    const [isLoading, setIsLoading] = useState(false);
    // Track if we're manually fetching to avoid duplicate requests
    const isFetchingRef = useRef(false);

    const {
      document: hookDocument,
      getSummaries,
      regenerateSummaries,
      isGenerating,
    } = useSummariesForPatient(displaySummaryType);

    const [localDocuments, setLocalDocuments] = useState<Array<ChartReviewResponse> | undefined>(
      undefined,
    );

    // Memoize document to prevent unnecessary re-renders
    const responses = useMemo(() => localDocuments || hookDocument, [localDocuments, hookDocument]);

    const [feedback, setFeedback] = useState<FeedbackRequest>(
      newBlankFeedbackRequest({ upid: patient.UPID }),
    );
    const { triggerFeedback } = useFeedback(feedback);

    const containerRef = useRef<HTMLDivElement>(null);
    const { breakpoints } = useContainerQuery({ containerRef });

    const { isDrawerOpen, openDrawer } = usePatientSummaryDrawer({ responses: responses ?? [] });

    const { navigate } = useZuiContext();
    const redirectTo = useCallback(
      (resource: OverviewResources) => {
        navigate(resource);
      },
      [navigate],
    );

    const [isEvidenceSetToVisible, setIsEvidenceSetToVisible] = useState(true);

    const handleGetSummaries = useCallback(async () => {
      try {
        return await getSummaries(displaySummaryType);
      } catch (error) {
        return error;
      }
    }, [getSummaries, displaySummaryType]);

    const handleOpenDrawerFromThumbs = (isThumbsUp: boolean | undefined) => {
      const newFeedback = { ...feedback, isThumbsUp };
      if (feedback.isThumbsUp !== isThumbsUp) {
        newFeedback.reasons = []; // Different set of reasons between thumbs up and down
      }
      openDrawer({
        feedback: newFeedback,
        setFeedback: setFeedback,
        triggerFeedback,
      });
    };

    const handleRegenerateSummaries = useCallback(async () => {
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const results = await regenerateSummaries(displaySummaryType);
        setLocalDocuments(results);
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        telemetry.trackError({
          message: 'Error regenerating summary',
          error: err,
        });
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    }, [regenerateSummaries, displaySummaryType, telemetry]);

    // create array of PrecomputedSummary components
    const precomputedSummaries = useMemo(() => {
      // If actively regenerating (manual button click), show a single, global loading indicator.
      if (isLoading || !responses || responses.length === 0) {
        return (
          <SetTimeProgressBar
            totalTimeSeconds={30}
            stages={{
              0: 'Finding patient data',
              20: 'Grouping data by medical relevancy',
              40: 'Generating summary',
            }}
          />
        );
      }

      // If not manually loading (isLoading is false):
      // Handle initial loading state or if the summary type isn't set yet,
      // and there are no responses yet.
      if ((!responses || responses.length === 0) && (isGenerating || !displaySummaryType)) {
        return (
          <SetTimeProgressBar
            totalTimeSeconds={30}
            stages={{
              0: 'Finding patient data',
              20: 'Grouping data by medical relevancy',
              40: 'Generating summary',
            }}
          />
        );
      }

      // If all loading is complete (isLoading is false, isGenerating is false)
      // and there are still no responses, the parent component will show NoDataError.
      // So, return null here to avoid rendering anything extra from this memoized value.
      if (!responses || responses.length === 0) {
        return null;
      }

      // If not loading and responses are available, map them.
      return responses.map((doc, idx) => (
        <PrecomputedSummary
          key={`summary-${doc.title ?? 'generated'}-${idx}`}
          onGenerateSummary={handleGetSummaries}
          document={doc}
          isGenerating={isGenerating || !displaySummaryType} // isLoading is handled globally above for regeneration
          className={twx(
            'rounded-lg border border-background-subtle border-rounded-lg p-4',
            className,
          )}
        />
      ));
    }, [responses, handleGetSummaries, isGenerating, isLoading, displaySummaryType, className]);

    return (
      <div ref={containerRef} className={tw`relative flex h-full flex-col px-2 pt-2`}>
        <Heading level="h1" className={tw`font-normal font-size-3xl`}>
          Your 5-Minute Briefing on {patient.displayFull}
        </Heading>
        {responses?.length === 0 ? (
          <NoDataError
            className={tw`my-4 rounded-lg bg-background-hover`}
            message="Sorry, we weren't able to generate a summary for this patient."
          />
        ) : (
          <TwoColumnContent
            leftColumn={{
              hidden: !(breakpoints.isAtLeast.md && isEvidenceSetToVisible),
              header: (
                <div className={tw`flex flex-1 items-center gap-2`}>
                  {breakpoints.isAtLeast.md && (
                    <button
                      type="button"
                      onClick={() => {
                        telemetry.trackInteraction('patient_summary_evidence_drawer_close');
                        setIsEvidenceSetToVisible(false);
                      }}
                      className={tw`flex h-4 w-4 items-center justify-center`}
                      aria-label="Close Evidence Drawer"
                    >
                      <FontAwesomeIcon
                        icon={faSidebar}
                        className={tw`text-content-icon hover:text-content-subtle`}
                      />
                    </button>
                  )}
                  <Heading
                    level="h2"
                    className={twx(
                      'line-clamp-2 flex items-center gap-2 text-content-subtle text-xs uppercase',
                    )}
                  >
                    Clinical Evidence
                  </Heading>
                </div>
              ),
              content: (
                <PatientOverview onSeeAllResources={redirectTo} className={tw`min-w-[320px]`} />
              ),
            }}
            rightColumn={{
              header: (
                <div className={tw`flex min-w-0 flex-1 justify-between gap-2`}>
                  <div className={tw`flex items-center gap-2`}>
                    {!isEvidenceSetToVisible && breakpoints.isAtLeast.md && (
                      <button
                        type="button"
                        onClick={() => {
                          telemetry.trackInteraction('patient_summary_evidence_drawer_open');
                          setIsEvidenceSetToVisible(true);
                        }}
                        className={tw`flex h-4 w-4 items-center justify-center`}
                      >
                        <FontAwesomeIcon
                          icon={faSidebar}
                          className={tw`text-content-icon hover:text-content-subtle`}
                        />
                      </button>
                    )}
                    <Heading
                      level="h2"
                      className={twx(
                        'line-clamp-2 flex w-fit items-center gap-2 text-content-subtle text-xs uppercase',
                      )}
                    >
                      Summary of all available information
                    </Heading>
                    <Tooltip
                      id="patient-summary-generated-time"
                      content={`Generated ${formatDateToLocalString(responses?.[0]?.createdAt)}`}
                    >
                      <FontAwesomeIcon
                        className={tw`block h-4 w-4 text-content-icon`}
                        icon={faInfoCircle}
                      />
                    </Tooltip>
                  </div>
                  <div className={twx('flex items-center gap-1')}>
                    <Button
                      variant="unstyled"
                      size="sm"
                      onClick={() => {
                        handleOpenDrawerFromThumbs(true);
                      }}
                      type="button"
                      aria-label="Thumbs up"
                      telemetryInteractionName="feedback_thumbs_up"
                      disabled={isDrawerOpen}
                    >
                      <FontAwesomeIcon
                        icon={faThumbsUp}
                        className={twx('text-content-icon transition-all duration-150', {
                          'hover:text-content-subtle': !isDrawerOpen,
                        })}
                      />
                    </Button>
                    <Button
                      variant="unstyled"
                      size="sm"
                      type="button"
                      aria-label="Thumbs down"
                      telemetryInteractionName="feedback_thumbs_down"
                      onClick={() => {
                        handleOpenDrawerFromThumbs(false);
                      }}
                      disabled={isDrawerOpen}
                    >
                      <FontAwesomeIcon
                        icon={faThumbsDown}
                        className={twx(
                          'scale-x-[-1] text-content-icon transition-all duration-150',
                          {
                            'hover:text-content-subtle': !isDrawerOpen,
                          },
                        )}
                      />
                    </Button>
                    {useRegenerateSummary && (
                      <Button
                        variant="unstyled"
                        size="sm"
                        type="button"
                        ariaLabel="Regenerate Summary"
                        onClick={handleRegenerateSummaries}
                      >
                        <FontAwesomeIcon icon={faRefresh} className={twx('text-content-icon')} />
                      </Button>
                    )}
                  </div>
                </div>
              ), // Right margin for thumbs up/down buttons
              content: (
                <div className={twx('space-y-4')}>
                  {precomputedSummaries}
                  <Card
                    className={twx('p-2 transition-all duration-300', {
                      'invisible opacity-0': isDrawerOpen,
                      'opacity-100': !isDrawerOpen,
                    })}
                  >
                    <div className={tw`space-y-4`}>
                      <Heading level="h4" className={tw`font-medium text-2xl text-content-subtle`}>
                        What Did You Think of this Summary?
                      </Heading>
                      <FeedbackThumbs
                        onChange={handleOpenDrawerFromThumbs}
                        selectedStateDisabled={true}
                      />
                    </div>
                  </Card>
                  {Array.isArray(responses) && responses.length > 0 && (
                    <div className={tw`pt-2 text-xs italic`}>
                      This health record summary is generated using AI technology and is intended
                      for informational purposes only. While efforts have been made to ensure
                      accuracy, the information provided may not capture all relevant details or
                      updates.
                    </div>
                  )}
                </div>
              ),
            }}
          />
        )}
      </div>
    );
  },
});
